import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Helper to extract meaningful error messages from various error types
function getErrorMessage(error) {
  // Server responded with an error
  if (error.response?.data?.detail) {
    return formatApiErrorDetail(error.response.data.detail);
  }
  
  // Network error (CORS, connection refused, timeout)
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    return "Unable to connect to the server. Please check your internet connection and try again.";
  }
  
  // Request was made but no response (timeout, server down)
  if (error.request && !error.response) {
    return "Server is not responding. Please try again in a moment.";
  }
  
  // CORS or other browser-level block
  if (error.message?.includes('CORS') || error.message?.includes('cross-origin')) {
    return "Connection blocked. Please refresh the page and try again.";
  }
  
  // Generic fallback with actual error if available
  return error.message || "Something went wrong. Please try again.";
}

// UTM parameter helper - captures and stores UTM params from URL
const getStoredUtmParams = () => {
  // First check URL for fresh UTM params
  const urlParams = new URLSearchParams(window.location.search);
  const utmFromUrl = {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_content: urlParams.get('utm_content'),
    utm_term: urlParams.get('utm_term')
  };
  
  // If we have UTM params in URL, store them
  const hasUtm = Object.values(utmFromUrl).some(v => v !== null);
  if (hasUtm) {
    sessionStorage.setItem('turfpulse_utm', JSON.stringify(utmFromUrl));
    return utmFromUrl;
  }
  
  // Otherwise, try to get from session storage (for users who navigated away from landing)
  try {
    const stored = sessionStorage.getItem('turfpulse_utm');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading UTM params:', e);
  }
  
  return {};
};

// Safe toast import — falls back to console if sonner isn't loaded at module init.
let _toast = null;
const toast = {
  error: (msg) => {
    try {
      if (!_toast) _toast = require('sonner').toast;
      _toast?.error?.(msg, { duration: 8000 });
    } catch (_) { console.warn('[auth]', msg); }
  },
};

/**
 * Called when refresh-token also fails (session truly expired).
 * Three jobs:
 *   1. Scream loudly — full-width persistent toast (not the subtle "login failed" flash)
 *   2. Preserve the user's location — stash `next=<current-path>` so post-login we return them
 *   3. Only redirect if they're on a protected route; leave them alone on public pages
 *
 * Why: the invisible-401-eats-my-draft failure mode is a credibility killer. A
 * visitor who loses work to a silent bug never comes back.
 */
function handleSessionExpired() {
  const path = window.location.pathname + window.location.search;
  // Public pages don't need a redirect — user may just be browsing unauthenticated.
  const publicPrefixes = ['/', '/blog', '/hey', '/pro', '/about', '/privacy', '/terms',
                          '/water-budget', '/catch-cup', '/byos', '/fungicide-cheat-sheet',
                          '/kit', '/try', '/try-diagnosis', '/login', '/register',
                          '/forgot-password', '/reset-password'];
  const isPublic = publicPrefixes.some((p) =>
    path === p || path.startsWith(`${p}?`) || path.startsWith(`${p}/`));
  if (isPublic) return; // silent — user isn't in a protected flow
  toast.error('Your session expired — redirecting to sign in. Your work is being preserved.');
  // Small delay so toast is visible before nav
  setTimeout(() => {
    const next = encodeURIComponent(path);
    window.location.href = `/?auth=1&next=${next}&reason=expired`;
  }, 1200);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use relative URL - centralized in config/api.js to prevent preview URL leaking into production
  const API_URL = '';
  // Note: API_URL is always '' because both preview (K8s ingress) and production (nginx) route /api to backend

  // Capture UTM params on app mount — runs once when a user lands on ANY page
  // with utm_source/utm_campaign/etc. in the URL. Stashes them in sessionStorage
  // so they survive route changes (e.g. landing on /round-rock-rebate?utm_source=nextdoor
  // → reading → clicking through to /register → registering 5 minutes later).
  // Without this, the params evaporate on the first internal navigation and
  // every signup looks like "direct" traffic. Critical for Nextdoor/FB ad ROI tracking.
  useEffect(() => {
    getStoredUtmParams();
  }, []);

  // Set up axios interceptor for automatic token refresh on 401
  useEffect(() => {
    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error) => {
      failedQueue.forEach(prom => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve();
        }
      });
      failedQueue = [];
    };

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Only retry on 401, skip auth endpoints to avoid loops
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/api/auth/refresh') &&
          !originalRequest.url?.includes('/api/auth/login') &&
          !originalRequest.url?.includes('/api/auth/me')
        ) {
          if (isRefreshing) {
            // Queue requests while refresh is in progress
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(() => axios(originalRequest));
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
            processQueue(null);
            return axios(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError);
            setUser(false);
            // Session unrecoverable — make it LOUD so the user notices immediately
            // instead of silently eating their form input.
            handleSessionExpired();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(data);
    } catch (error) {
      // Access token expired — try to silently refresh using the 7-day refresh token
      if (error.response?.status === 401) {
        try {
          const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
            withCredentials: true
          });
          setUser(data);
        } catch {
          setUser(false);
        }
      } else {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name, grassType) => {
    try {
      // Get UTM params to track campaign source
      const utmParams = getStoredUtmParams();
      
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { 
          email, 
          password, 
          name, 
          grass_type: grassType,
          ...utmParams  // Include UTM tracking data
        },
        { withCredentials: true }
      );
      setUser(data);
      // Clear UTM params after successful registration
      sessionStorage.removeItem('turfpulse_utm');
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: getErrorMessage(error) };
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: getErrorMessage(error) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
    }
  };

  // Set user directly from API response data (used by Admin Backdoor flow)
  const loginWithData = (data) => {
    setUser(data);
  };

  // ─── WebAuthn / Passkey methods ──────────────────────────────────────
  // Use @simplewebauthn/browser to drive the platform biometric prompt.
  // Successful login mints the same access_token cookie the password
  // flow does, so the rest of the app keeps working unchanged.
  //
  // `_withTimeout` guards against the rare case where the OS prompt is
  // dismissed silently (or the page is in a headless context with no
  // virtual authenticator) so the UI doesn't sit on "Prompting…" forever.
  const _withTimeout = (promise, ms = 60_000) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(
      () => reject(Object.assign(new Error('passkey_timeout'), { name: 'TimeoutError' })),
      ms,
    )),
  ]);

  const registerPasskey = async (deviceLabel = null) => {
    try {
      const { startRegistration, browserSupportsWebAuthn } = await import('@simplewebauthn/browser');
      if (!browserSupportsWebAuthn()) {
        return { success: false, error: 'This device does not support passkeys yet.' };
      }
      const { data: options } = await axios.post(
        `${API_URL}/api/webauthn/register/begin`,
        { device_label: deviceLabel },
        { withCredentials: true },
      );
      const att = await _withTimeout(startRegistration({ optionsJSON: options }));
      const { data: created } = await axios.post(
        `${API_URL}/api/webauthn/register/complete`,
        { credential: att, device_label: deviceLabel },
        { withCredentials: true },
      );
      return { success: true, credential: created };
    } catch (e) {
      const msg = e?.name === 'NotAllowedError'
        ? 'Passkey enrollment was cancelled.'
        : e?.name === 'TimeoutError'
        ? 'Passkey prompt timed out. Please try again.'
        : (e?.response?.data?.detail || e?.message || 'Could not register passkey');
      return { success: false, error: String(msg) };
    }
  };

  const loginWithPasskey = async (email) => {
    try {
      const { startAuthentication, browserSupportsWebAuthn } = await import('@simplewebauthn/browser');
      if (!browserSupportsWebAuthn()) {
        return { success: false, error: 'This device does not support passkeys yet.' };
      }
      const { data: options } = await axios.post(
        `${API_URL}/api/webauthn/login/begin`,
        { email },
        { withCredentials: true },
      );
      const assertion = await _withTimeout(startAuthentication({ optionsJSON: options }));
      const { data: userData } = await axios.post(
        `${API_URL}/api/webauthn/login/complete`,
        { email, credential: assertion },
        { withCredentials: true },
      );
      setUser(userData);
      return { success: true };
    } catch (e) {
      if (e?.response?.status === 404) {
        return { success: false, error: 'no_passkey' };
      }
      const msg = e?.name === 'NotAllowedError'
        ? 'Sign-in was cancelled.'
        : e?.name === 'TimeoutError'
        ? 'Sign-in prompt timed out. Please try again.'
        : (e?.response?.data?.detail || e?.message || 'Passkey sign-in failed');
      return { success: false, error: String(msg) };
    }
  };

  const hasPasskey = async (email) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/webauthn/has-passkey`,
        { params: { email } },
      );
      return !!data?.available;
    } catch {
      return false;
    }
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    checkAuth,
    loginWithData,
    registerPasskey,
    loginWithPasskey,
    hasPasskey,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
